import { useQuery } from "@tanstack/react-query";
import { getDisplayLanguageCodes } from "@/lib/constants/languages";

interface TranslationData {
  [key: string]: any;
}

interface UseTranslationsOptions {
  language: string;
  namespace: string;
}

const TRANSLATION_NAMESPACES = [
  "common",
  "landingPage",
  "communicationGenerator",
  "resourceCustomiser",
  "researcher",
  "lessonLab",
  "settings",
  "pricing",
  "auth",
  "results",
  "sitemap",
  "templates",
  "errors",
] as const;

const loadRemoteTranslations = async (url: string): Promise<TranslationData> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load translations: ${response.status}`);
  }
  return response.json();
};

const loadLocalTranslations = async (language: string, namespace: string): Promise<TranslationData> => {
  try {
    const localUrl = `/locales/${language}/${namespace}.json`;
    const response = await fetch(localUrl);
    if (!response.ok) {
      throw new Error(`Local translation not found: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.warn(`Failed to load local translations for ${language}/${namespace}:`, error);
    throw error;
  }
};

const loadTranslationsWithFallback = async (language: string, namespace: string): Promise<TranslationData> => {
  const remoteUrl = import.meta.env.DEV
    ? `https://localhost:5174/locales/locales/${language}/${namespace}.json`
    : `${(import.meta.env.VITE_LANGUAGE_URL as string).replace("/{{lng}}/{{ns}}.json", '')}/${language}/${namespace}.json`;

  try {
    return await loadRemoteTranslations(encodeURI(remoteUrl));
  } catch (remoteError) {
    console.warn(`Remote translation failed for ${language}/${namespace}, falling back to local:`, remoteError);

    try {
      // Fallback to local
      return await loadLocalTranslations(language, namespace);
    } catch (localError) {
      console.error(`Both remote and local translation loading failed for ${language}/${namespace}:`, localError);
      throw new Error(`Failed to load translations for ${language}/${namespace}`);
    }
  }
};

export const useTranslationFile = ({ language, namespace }: UseTranslationsOptions) => {
  return useQuery({
    queryKey: ['translations', language, namespace],
    queryFn: () => loadTranslationsWithFallback(language, namespace),
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: (failureCount) => {
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useAllTranslations = (language: string) => {
  const supportedLanguages = getDisplayLanguageCodes();

  const isValidLanguage = supportedLanguages.includes(language as any);

  return useQuery({
    queryKey: ['all-translations', language],
    queryFn: async () => {
      if (!isValidLanguage) {
        throw new Error(`Unsupported language: ${language}`);
      }

      const results = await Promise.allSettled(
        TRANSLATION_NAMESPACES.map(namespace =>
          loadTranslationsWithFallback(language, namespace)
        )
      );

      const translations: Record<string, TranslationData> = {};

      results.forEach((result, index) => {
        const namespace = TRANSLATION_NAMESPACES[index];
        if (result.status === 'fulfilled') {
          translations[namespace] = result.value;
        } else {
          console.error(`Failed to load ${namespace} for ${language}:`, result.reason);
          translations[namespace] = {};
        }
      });

      return translations;
    },
    enabled: isValidLanguage,
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    retry: 1,
  });
};

// Export namespaces for external use
export { TRANSLATION_NAMESPACES };
